import { NSTable } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { numeralFormatter, numeralFormatterCurrency } from 'ns_libs/formatter';
import useHasBuilderDepositSource from 'hooks/useHasBuilderDepositSource';
import { SaleFrequency } from 'views/ProFormaTable/types';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { getSalesPeriodLabel } from './helpers';
import './ProFormaSalesSchedule.scss';
import ConstructionEndsAfterSaleWarning from '../../ProFormaDevelopment/ConstructionEndsAfterSaleWarning/ConstructionEndsAfterSaleWarning';

const TH_CLASS = 'NSTable__thead__tr__th align-middle py-2 px-3';
const TD_CLASS = 'NSTable__tbody__tr__td align-middle px-3 py-2';
const TFOOT_TD_CLASS = 'NSTable__tfoot__tr__td align-middle px-3 py-2 text-dark font-weight-bold';
const CUMULATIVE_LOTS_CELL = (
    'ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--summary '
    + 'ProFormaSalesSchedule__cell--cumulative-lots'
);

const formatSignedCurrency = (value: number) => (
    value < 0 ? `(${numeralFormatterCurrency(Math.abs(value))})` : numeralFormatterCurrency(value)
);

const ProFormaSalesSchedule = () => {
    const {
        salesSchedule, disposition, proForma, uses, sources, sourceTypes,
    } = useProFormaSocketContext();
    const periods = salesSchedule?.periods ?? [];
    const isEmpty = periods.length === 0;

    const frequency: SaleFrequency = disposition?.saleFrequency ?? 'quarterly';
    const lotColumns = periods[0]?.lotSales ?? [];
    const columnTotals = lotColumns.map(
        (_, lotIdx) => periods.reduce((total, period) => total + (period.lotSales[lotIdx]?.lotsSold ?? 0), 0),
    );
    const hasBuilderDepositSource = useHasBuilderDepositSource(sources, sourceTypes);
    // Period + lot columns + cumulative lots + revenue (+ builder deposit credit + net proceeds).
    const totalColumns = lotColumns.length + (hasBuilderDepositSource ? 5 : 3);

    return (
        <>
            <RenderIf isTrue={proForma?.constructionEndsAfterLastSale === true}>
                <ConstructionEndsAfterSaleWarning
                    constructionEndDate={uses?.developmentEndDate}
                    lastSaleDate={proForma?.saleDate}
                />
            </RenderIf>
            <div className="m-3 scrollable-container ProFormaSalesSchedule__scroll">
                <NSTable className="NSTable mb-0">
                    <thead className="NSTable__thead">
                        <tr className="NSTable__thead__tr">
                            <th className={`${TH_CLASS} ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--period`}>Period</th>
                            {lotColumns.map(lot => (
                                <th
                                    key={lot.lotId ?? lot.lotName ?? lot.lotWidth}
                                    className={`${TH_CLASS} text-right ProFormaSalesSchedule__cell`}
                                >
                                    {lot.lotName || `${lot.lotWidth ?? 0} ft`}
                                </th>
                            ))}
                            <th className={`${TH_CLASS} text-right ${CUMULATIVE_LOTS_CELL}`}>Cumulative lots</th>
                            <th className={`${TH_CLASS} text-right ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--summary`}>
                                Revenue
                            </th>
                            <RenderIf isTrue={hasBuilderDepositSource}>
                                <th className={`${TH_CLASS} text-right ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--summary`}>
                                    Builder deposit credit
                                </th>
                                <th className={`${TH_CLASS} text-right ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--summary`}>
                                    Net proceeds
                                </th>
                            </RenderIf>
                        </tr>
                    </thead>
                    <tbody className="NSTable__tbody">
                        <RenderIf isTrue={isEmpty}>
                            <tr className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                                <td colSpan={totalColumns} className="NSTable__tbody__tr__td text-center text-muted py-4">
                                    Set lot sizes, pricing, takedown rates, and a sales start date to generate the sales schedule.
                                </td>
                            </tr>
                        </RenderIf>
                        <RenderIf isTrue={!isEmpty}>
                            {periods.map(period => (
                                <tr key={period.period} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                                    <td className={`${TD_CLASS} ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--period`}>
                                        {getSalesPeriodLabel(
                                            period.period,
                                            frequency,
                                            disposition?.salesStart,
                                            proForma?.constructionStart,
                                            period.periodStartDate,
                                        )}
                                    </td>
                                    {period.lotSales.map(lot => (
                                        <td
                                            key={lot.lotId ?? lot.lotName ?? lot.lotWidth}
                                            className={`${TD_CLASS} text-right ProFormaSalesSchedule__cell`}
                                        >
                                            {numeralFormatter(lot.lotsSold, '0,0')}
                                        </td>
                                    ))}
                                    <td className={`${TD_CLASS} text-right ${CUMULATIVE_LOTS_CELL}`}>
                                        {numeralFormatter(period.cumulativeLots, '0,0')}
                                    </td>
                                    <td className={`${TD_CLASS} text-right ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--summary`}>
                                        {numeralFormatterCurrency(period.revenue)}
                                    </td>
                                    <RenderIf isTrue={hasBuilderDepositSource}>
                                        <td className={`${TD_CLASS} text-right ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--summary`}>
                                            {formatSignedCurrency(period.builderDepositCredit)}
                                        </td>
                                        <td className={`${TD_CLASS} text-right ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--summary`}>
                                            {formatSignedCurrency(period.netProceeds)}
                                        </td>
                                    </RenderIf>
                                </tr>
                            ))}
                        </RenderIf>
                    </tbody>
                    <RenderIf isTrue={!isEmpty}>
                        <tfoot className="NSTable__tfoot">
                            <tr className="NSTable__tfoot__tr">
                                <td className={`${TFOOT_TD_CLASS} ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--period`}>Total</td>
                                {columnTotals.map((total, lotIdx) => (
                                    <td
                                        key={lotColumns[lotIdx].lotId ?? lotColumns[lotIdx].lotName ?? lotIdx}
                                        className={`${TFOOT_TD_CLASS} text-right ProFormaSalesSchedule__cell`}
                                    >
                                        {numeralFormatter(total, '0,0')}
                                    </td>
                                ))}
                                <td className={`${TFOOT_TD_CLASS} text-right ${CUMULATIVE_LOTS_CELL}`}>
                                    {numeralFormatter(salesSchedule?.totalLotsSold ?? 0, '0,0')}
                                </td>
                                <td className={`${TFOOT_TD_CLASS} text-right ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--summary`}>
                                    {numeralFormatterCurrency(salesSchedule?.totalRevenue ?? 0)}
                                </td>
                                <RenderIf isTrue={hasBuilderDepositSource}>
                                    <td className={`${TFOOT_TD_CLASS} text-right ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--summary`}>
                                        {formatSignedCurrency(salesSchedule?.totalBuilderDepositCredit ?? 0)}
                                    </td>
                                    <td className={`${TFOOT_TD_CLASS} text-right ProFormaSalesSchedule__cell ProFormaSalesSchedule__cell--summary`}>
                                        {formatSignedCurrency(salesSchedule?.totalNetProceeds ?? 0)}
                                    </td>
                                </RenderIf>
                            </tr>
                        </tfoot>
                    </RenderIf>
                </NSTable>
            </div>
        </>
    );
};

export default ProFormaSalesSchedule;
